import { BaseSyntheticEvent, useEffect, useState } from 'react'
import Button from '../../../common/components/marketing/buttons/Button'
import { CustomImageModal } from '../modals/CustomImageModal'
import { PredesignsModal } from '../modals/PredesignsModal'
import * as yup from 'yup'
import { ValidationError } from 'yup'
import { BuildACardStep, SimpleFieldWrapper } from '../StepElement'
import { FormErrorDisplay } from '../FormErrorDisplay'
import postLanguageCheck from '../../../common/queries/PostLanguageCheckQuery'
import { maxSingleCardLoad, minCardLoad } from '../../checkout/strings'
import {
    cardTextMax,
    cardTextMin,
    CUSTOM_IMAGE_TYPE,
    embossRegexYup,
    PREDESIGN_IMAGE_TYPE,
} from '../constants'
import {
    CardImageButtonWrapper,
    HeaderParagraphText,
} from '@/src/modules/build-a-card/StyledComponents'
import EmbossFields from '@/src/modules/build-a-card/inputs/EmbossFields'
import { useBasicGiftCardContext } from '@/src/common/contexts/BasicGiftCardContext'
import DefaultDenominationOptions from '@/src/modules/build-a-card/inputs/DefaultDenominationOptions'
import { usePlasticGiftQuantityContext } from '@/src/common/contexts/PlasticGiftQuantityContext'
import QuantitySelectorButton from '@/src/common/components/elements/Buttons/QuantitySelectorButton'
import KYCFlowMaxErrors from '@/src/common/components/elements/Alerts/KYCFlowMaxErrors'
import { useCartContext } from '@/src/common/contexts/CartContext'
import { useRouter } from 'next/router'
import PurchaseFlowMaxAlert from '@/src/common/components/elements/Alerts/PurchaseFlowMaxAlert'
import { validateDenomOnChange } from '@/src/modules/build-a-card/validations/validateDenom'
import RequiredAsterisk from '@/src/common/components/elements/Forms/RequiredAsterisk'
import { DenomInputWithLabel } from '@/src/common/components/elements/Forms/DenomInputWithLabel'
import KYCMaxPurchaseButtons from '@/src/modules/kyc/buttons/KYCMaxPurchaseButtons'

export interface IPhysicalClosedLoopStep2 {
    setCloudinaryUrl: Function
    setSkipPackaging: Function
    setPlaceholderClicked?: Function
    placeholderIsClicked?: boolean
}

const plasticStep1Schema = yup.object({
    denomination: yup
        .string()
        .label('Amount')
        .required()
        .test(
            'denomValueLegit',
            `Amount must be between $${minCardLoad} and $${maxSingleCardLoad}`,
            (value: string | undefined) => {
                if (!value) return false
                const floatVal = parseFloat(value?.replace(/[^0-9.]+/g, ''))
                return minCardLoad <= floatVal && floatVal <= maxSingleCardLoad //@FUTURE: Dynamic data from server side.
            }
        ),
    designImage: yup
        .string()
        .label('Design')
        .required('Please upload a photo or select a pre-designed image')
        .url(),
    messageOnCard: yup
        .string()
        .label('Message')
        .required()
        .matches(
            embossRegexYup,
            'Only alphanumeric characters and hyphens are allowed'
        )
        .min(
            cardTextMin,
            `Message must contain at least ${cardTextMin} characters`
        )
        .max(
            cardTextMax,
            `Message cannot contain more than ${cardTextMax} characters`
        )
        .test(
            'badWords',
            'Contains words or phrases that are not permitted',
            postLanguageCheck
        ),
    nameOnCard: yup
        .string()
        .label('Name')
        .required()
        .matches(
            embossRegexYup,
            'Only alphanumeric characters and hyphens are allowed'
        )
        .min(
            cardTextMin,
            `Name on card must contain at least ${cardTextMin} characters`
        )
        .max(
            cardTextMax,
            `Name on card cannot contain more than ${cardTextMax} characters`
        )
        .test(
            'badWords',
            'Contains words or phrases that are not permitted',
            postLanguageCheck
        ),
})

export const GiftCustomizationForm = ({
    setCloudinaryUrl,
    setSkipPackaging,
    setPlaceholderClicked = undefined,
    placeholderIsClicked = false,
}: IPhysicalClosedLoopStep2) => {
    const {
        currentStep,
        setCurrentStep,
        predesigns,
        nameOnCard,
        setNameOnCard,
        messageOnCard,
        setMessageOnCard,
        hasGreetingCard,
        denomination,
        designImage,
        setDenomination,
        setDesignImage,
        setDesignName,
        setCustomDesignObject,
        setPredesignUuid,
        predesignUuid,
        designName,
        selectedImageType,
        setSelectedImageType,
    } = useBasicGiftCardContext()
    const { giftState, dispatch, KYCMinMaxLimits } =
        usePlasticGiftQuantityContext()
    const {
        loadAmountExceedsKYCMax,
        numberOfCardsExceedsKYCMax,
        loadAmountMeetsKYCMin,
        numberOfCardsMeetsKYCMin,
    } = KYCMinMaxLimits
    const { isEditing } = giftState
    const { cartTotalCards, setCartFlyoutOpen } = useCartContext()

    const router = useRouter()
    const isPersonalizedFlow: boolean = router.pathname.includes('personalized')

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [skipIsLoading, setSkipIsLoading] = useState<boolean>(false)
    const [customImageModalIsOpen, setCustomImageModalIsOpen] =
        useState<boolean>(false)
    const [predesignsModalIsOpen, setPredesignsModalIsOpen] =
        useState<boolean>(false)
    const [formErrors, setFormErrors] = useState<ValidationError[]>([])
    const [displayLiveErrors, setDisplayLiveErrors] = useState<boolean>(false)
    const defaultRecipient = !!giftState?.listOfRecipients
        ? giftState?.listOfRecipients[0]
        : undefined

    const showKYCMaxError: boolean =
        loadAmountExceedsKYCMax || numberOfCardsExceedsKYCMax
    const showKycLimitAlert =
        (loadAmountMeetsKYCMin || numberOfCardsMeetsKYCMin) && !showKYCMaxError
    const hasCustomImage = designName.toLowerCase() == CUSTOM_IMAGE_TYPE

    useEffect(() => {
        if (placeholderIsClicked) {
            setCustomImageModalIsOpen(true)
        }
    }, [placeholderIsClicked])

    // if display live errors, validate fields on change
    useEffect(() => {
        if (!displayLiveErrors) {
            return
        }
        plasticStep1Schema
            .validate(
                {
                    denomination,
                    designImage,
                    nameOnCard,
                    messageOnCard,
                },
                { abortEarly: false }
            )
            .then(() => {
                setFormErrors([])
            })
            .catch((err) => {
                setFormErrors(err.inner)
            })
    }, [
        denomination,
        designImage,
        nameOnCard,
        messageOnCard,
        displayLiveErrors,
    ])

    const continueToNextStep = async ({
        skipPackaging = false,
    }: {
        skipPackaging: boolean
    }) => {
        // on continue, validate form fields
        await plasticStep1Schema
            .validate(
                {
                    denomination,
                    designImage,
                    nameOnCard,
                    messageOnCard,
                },
                {
                    abortEarly: false,
                    context: { checkLanguage: true },
                }
            )
            .then(() => {
                setFormErrors([])
                setSkipPackaging(skipPackaging)
                if (skipPackaging) {
                    hasGreetingCard ? setCurrentStep(4) : setCurrentStep(3)
                    return
                }
                setCurrentStep(2)
            })
            .catch((err) => {
                // if failure, set form errors and display live errors
                setFormErrors(err.inner)
                setDisplayLiveErrors(true)
            })
    }

    return (
        <BuildACardStep active={currentStep === 1}>
            <SimpleFieldWrapper>
                <DefaultDenominationOptions
                    denomination={denomination}
                    onDenominationChange={(denom) =>
                        validateDenomOnChange({
                            denom,
                            setDenomination,
                            formErrors,
                            setFormErrors,
                            minDenom: minCardLoad,
                            maxDenom: maxSingleCardLoad,
                        })
                    }
                    minMaxAndFixed={{
                        brand_min: minCardLoad,
                        brand_max: maxSingleCardLoad,
                    }}
                    label={'1. Choose an amount for each of your gifts:'}
                />
                <DenomInputWithLabel
                    name={'denomination'}
                    label={'Custom Amount'}
                    placeholder={`Enter an amount $${minCardLoad} - $${maxSingleCardLoad}`}
                    value={denomination}
                    onChange={(e: BaseSyntheticEvent) => {
                        const denom = e.target.value
                        validateDenomOnChange({
                            denom,
                            setDenomination,
                            formErrors,
                            setFormErrors,
                            minDenom: minCardLoad,
                            maxDenom: maxSingleCardLoad,
                        })
                    }}
                    onBlur={() => {
                        // if denom set, adjust format to ###.##
                        if (denomination) {
                            setDenomination(parseFloat(denomination).toFixed(2))
                        }
                    }}
                    type={'number'}
                    aria-placeholder={'Enter a custom amount'}
                    min={minCardLoad}
                    max={maxSingleCardLoad}
                />
                <FormErrorDisplay
                    formErrors={formErrors}
                    path={'denomination'}
                />
            </SimpleFieldWrapper>
            {!isEditing && (
                <QuantitySelectorButton
                    giftState={giftState}
                    dispatch={dispatch}
                    defaultRecipient={defaultRecipient}
                    label={'2. Choose the quantity:'}
                    wrap={true}
                    maxWidth={'50%'}
                />
            )}
            <SimpleFieldWrapper>
                <HeaderParagraphText>
                    {isEditing ? '2' : '3'}. Choose wrapper design:
                    {isPersonalizedFlow && <RequiredAsterisk />}
                </HeaderParagraphText>
                <FormErrorDisplay
                    formErrors={formErrors}
                    path={'designImage'}
                    variant={'error-message'}
                />
                <CardImageButtonWrapper variant={'wrap'}>
                    <Button
                        label={'Upload a photo'}
                        onClick={() => setCustomImageModalIsOpen(true)}
                        variant={
                            selectedImageType == CUSTOM_IMAGE_TYPE ||
                            (hasCustomImage && isEditing)
                                ? 'graySquareButtonActive'
                                : 'graySquareButton'
                        }
                        iconType={'image'}
                    />
                    <CustomImageModal
                        isOpen={customImageModalIsOpen}
                        setIsOpen={setCustomImageModalIsOpen}
                        setDesignImage={setDesignImage}
                        setCustomDesignObject={setCustomDesignObject}
                        setPredesignUuid={setPredesignUuid}
                        setPlaceholderClicked={setPlaceholderClicked}
                        setDesignName={setDesignName}
                        setSelectedImageType={setSelectedImageType}
                    />
                    <Button
                        label={'Choose a design'}
                        onClick={() => setPredesignsModalIsOpen(true)}
                        variant={
                            selectedImageType == PREDESIGN_IMAGE_TYPE ||
                            (!hasCustomImage && isEditing)
                                ? 'graySquareButtonActive'
                                : 'graySquareButton'
                        }
                        iconType={'upload'}
                    />
                </CardImageButtonWrapper>
                <PredesignsModal
                    isOpen={predesignsModalIsOpen}
                    setIsOpen={setPredesignsModalIsOpen}
                    setDesignImage={setDesignImage}
                    setCustomDesignObject={setCustomDesignObject}
                    predesignUuid={predesignUuid}
                    setPredesignUuid={setPredesignUuid}
                    predesigns={predesigns}
                    setDesignName={setDesignName}
                    setSelectedImageType={setSelectedImageType}
                />
            </SimpleFieldWrapper>
            <EmbossFields
                nameOnCard={nameOnCard}
                setNameOnCard={setNameOnCard}
                messageOnCard={messageOnCard}
                setMessageOnCard={setMessageOnCard}
                formErrors={formErrors}
                headerText={`${isEditing ? '3' : '4'}. Customize text on card:`}
            />
            {showKYCMaxError && <KYCFlowMaxErrors />}
            {showKycLimitAlert && <PurchaseFlowMaxAlert />}
            <SimpleFieldWrapper>
                {showKYCMaxError ? (
                    <KYCMaxPurchaseButtons
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        cartTotalCards={cartTotalCards}
                        setCartFlyoutOpen={setCartFlyoutOpen}
                        router={router}
                    />
                ) : (
                    <>
                        <Button
                            label={'Next'}
                            showArrow={true}
                            textTransform={'uppercase'}
                            width={isEditing ? 'wide' : 'standard'}
                            onClick={() => {
                                setIsLoading(true)
                                setCloudinaryUrl(designImage)
                                continueToNextStep({
                                    skipPackaging: false,
                                }).then(() => {
                                    setIsLoading(false)
                                })
                            }}
                            isLoading={isLoading}
                            disabled={isLoading || skipIsLoading}
                        />
                        {isEditing && (
                            <Button
                                label={'Keep original packaging'}
                                showArrow={true}
                                textTransform={'uppercase'}
                                variant={'primaryOutline'}
                                width={'wide'}
                                onClick={() => {
                                    setSkipIsLoading(true)
                                    setCloudinaryUrl(designImage)
                                    continueToNextStep({
                                        skipPackaging: true,
                                    }).then(() => {
                                        setSkipIsLoading(false)
                                    })
                                }}
                                isLoading={skipIsLoading}
                                disabled={skipIsLoading || isLoading}
                                style={{ marginTop: 10 }}
                            />
                        )}
                    </>
                )}
            </SimpleFieldWrapper>
        </BuildACardStep>
    )
}
